import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import PageHeader from "src/components/common/PageHeader";
import Form, { FormField } from "src/components/ui/Form";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as productActions from "src/redux/slices/productSlice";
import { toast } from "react-toastify";
import categoryApis from "src/api/category";
import { Category } from "src/types/category.types";
import { Product } from "src/types/product.types";

interface ProductLocationState {
  product?: Product & {
    categoryId?: { _id?: string; name?: string } | string;
    subCategoryId?: { _id?: string; name?: string } | string;
  };
}

const ProductForm: React.FC = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [requestedList, setRequestedList] = useState(false);

  const dispatch = useAppDispatch();
  const { createProduct, editProduct, productList } = useAppSelector(
    (state) => state.product
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);

  const locationState = location.state as ProductLocationState | undefined;
  const productFromState = locationState?.product || null;

  const productFromList = useMemo(() => {
    if (!productId) return null;
    return (
      productList?.data?.data?.find((item) => item?._id === productId) || null
    );
  }, [productId, productList?.data?.data]);

  const activeProduct = productFromState || productFromList;

  const normalizedInitialState = useMemo<{
    categoryId?: string;
    subCategoryId?: string;
  } & Record<string, any>>(() => {
    if (!activeProduct) return {};
    const categoryId =
      typeof activeProduct.categoryId === "string"
        ? activeProduct.categoryId
        : activeProduct.categoryId?._id || "";
    const subCategoryId =
      typeof activeProduct.subCategoryId === "string"
        ? activeProduct.subCategoryId
        : activeProduct.subCategoryId?._id || "";

    return {
      ...activeProduct,
      categoryId,
      subCategoryId,
      gstRate: String(activeProduct.gstRate ?? 18),
    };
  }, [activeProduct]);

  const parentCategories = categories;
  const selectedParent =
    parentCategories.find((cat) => cat._id === selectedCategory) || null;
  const subCategories = selectedParent?.subCategories || [];

  useEffect(() => {
    if (normalizedInitialState.categoryId && !selectedCategory) {
      setSelectedCategory(normalizedInitialState.categoryId);
    }
  }, [normalizedInitialState.categoryId, selectedCategory]);

  useEffect(() => {
    const getAllCategories = async () => {
      const payload = {
        page: 1,
        limit: 1000,
        status: "active" as const,
      };
      const response = await categoryApis.getCategoryList(payload);

      if (response && response.data) {
        const parents = (response?.data?.data || response?.data || []) as Category[];
        setCategories(parents);
      }
    };

    getAllCategories();
  }, []);

  useEffect(() => {
    if (productId && !activeProduct && !requestedList) {
      dispatch(
        productActions.getProductListRequest({
          page: 1,
          limit: 1000,
        })
      );
      setRequestedList(true);
    }
  }, [productId, activeProduct, requestedList, dispatch]);

  useEffect(() => {
    if (productId && requestedList && !activeProduct && productList?.data) {
      toast.error("Product not found");
      navigate("/app/product/list");
    }
  }, [productId, requestedList, activeProduct, productList?.data, navigate]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (createProduct.data) {
      toast.success("Product created successfully");
      handleClose();
    } else if (createProduct.error) {
      toast.error(`Error: ${createProduct.error}`);
    }
  }, [createProduct]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editProduct.data) {
      toast.success("Product modified successfully");
      handleClose();
    } else if (editProduct.error) {
      toast.error(`Error: ${editProduct.error}`);
    }
  }, [editProduct]);

  const handleClose = () => {
    dispatch(productActions.resetCreateProduct());
    dispatch(productActions.resetEditProduct());
    navigate("/app/product/list");
  };

  // ✅ Define all form fields
  const fields: FormField[] = useMemo(
    () => [
      {
        label: "Product Name",
        name: "name",
        type: "text",
        placeholder: "Enter product name",
        validations: { required: true },
      },
      {
        label: "Product Description",
        name: "description",
        type: "textarea",
        placeholder: "Enter product description",
        validations: { required: true },
      },
      {
        label: "SKU(optional)",
        name: "sku",
        type: "text",
        placeholder: "Enter SKU(optional)",
        validations: { required: false },
      },
      {
        label: "HSN Code (optional)",
        name: "hsnCode",
        type: "text",
        placeholder: "Enter HSN code",
        validations: { required: false },
      },
      {
        label: "Category",
        name: "categoryId",
        type: "dropdown",
        placeholder: "Select Category",
        options: parentCategories.map((cat) => ({
          label: cat.name || "",
          value: cat._id || "",
        })),
        onChange: (value: string) => setSelectedCategory(value),
        validations: { required: true },
      },
      {
        label: "Subcategory (Optional)",
        name: "subCategoryId",
        type: "dropdown",
        placeholder: "Select Subcategory (optional)",
        options: subCategories.map((cat) => ({
          label: cat.name || "",
          value: cat._id || "",
        })),
        validations: { required: false },
      },
      {
        label: "MRP",
        name: "mrp",
        type: "number",
        placeholder: "Enter product MRP",
        validations: { required: true, min: 1 },
      },
      {
        label: "Selling Price",
        name: "sellingPrice",
        type: "number",
        placeholder: "Enter product Selling Price",
        validations: { required: true, min: 1 },
      },
      {
        label: "Min Stock Quantity",
        name: "minStock",
        type: "number",
        placeholder: "Enter product Min Stock Quantity",
        validations: { required: true, min: 1 },
      },
      {
        label: "GST Rate",
        name: "gstRate",
        type: "dropdown",
        placeholder: "Select GST Rate",
        options: [
          { label: "0% (Exempt)", value: "0" },
          { label: "5%", value: "5" },
          { label: "12%", value: "12" },
          { label: "18%", value: "18" },
          { label: "28%", value: "28" },
        ],
        defaultValue: "18",
        validations: { required: true },
      },
    ],
    [parentCategories, subCategories]
  );

  // ✅ Form submission handler
  const handleSubmit = (data: Record<string, any>) => {
    if (productId || activeProduct?._id) {
      dispatch(
        productActions.editProductRequest({
          ...(data as any),
          _id: productId || activeProduct?._id,
        })
      );
    } else {
      dispatch(
        productActions.createProductRequest({
          ...data,
        })
      );
    }
  };

  return (
    <DashboardLayout
      meta={{
        title: productId ? "Edit Product | Invyntra" : "Create Product | Invyntra",
        description: "Create or edit products.",
        keywords: "products, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={productId ? "Edit Product" : "Create Product"}
          showBack={!!productId}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Products", path: "/app/product/list" },
            { label: productId ? "Edit Product" : "Create Product", path: "" },
          ]}
        />

        <Card>
          <Form
            initialState={normalizedInitialState as any}
            fields={fields}
            onSubmit={handleSubmit}
            onCancel={() => navigate("/app/product/list")}
            submitLabel={productId ? "Update Product" : "Create Product"}
            loading={createProduct.loading || editProduct.loading}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ProductForm;
